import React from 'react'
import { Signal } from '../utils/signals'
import { WidgetConfig } from './WidgetConfiguration'
import posthog from 'posthog-js'
import { useAppCtx } from '../hooks/use-app-ctx'

type Props = {
  children: React.ReactNode
  widgetId: string
  configSignal: Signal<WidgetConfig>
}

type State = {error: undefined | Error}

export function WidgetErrorBoundary(props: Props) {
  const appCtx = useAppCtx()
  return (
    <WidgetErrorBoundaryComponent {...props}
      displayChromiumTranslateErrors={appCtx.displayChromiumTranslateErrors}
    />
  )
}
class WidgetErrorBoundaryComponent extends React.Component<
  (Props & {displayChromiumTranslateErrors: boolean}), State
> {
  state: State = {error: undefined}

  static getDerivedStateFromError(error: Error): State {
    return {error}
  }

  componentDidMount() {
    this.props.configSignal.connect(this.configUpdated)
  }

  componentWillUnmount() {
    this.props.configSignal.disconnect(this.configUpdated)
  }

  configUpdated = () => {
    setTimeout(() => this.setState({error: undefined}))
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Widget encountered an error', error, errorInfo)
    posthog.captureException(error)
  }

  render(): React.ReactNode {
    if (isConsideredValid(this.state.error, this.props.displayChromiumTranslateErrors)) {

      return (
        <div role="alert" className="p-4 bg-red-100 text-red-800 rounded-md">
          <h2 className="font-bold mb-2">Something went wrong.</h2>
          <pre className="text-sm whitespace-pre-wrap mb-3">
            {this.state.error?.message ?? 'Unknown error'}
          </pre>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * see [github issue "Make React resilient to DOM mutations from Google Translate"](https://github.com/facebook/react/issues/11538)
 */
function isConsideredValid(
  error: Error | undefined,
  displayChromiumTranslateErrors: boolean,
): boolean {
  if (
    !displayChromiumTranslateErrors
    && error?.message.startsWith("Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.")
  ) return false

  return Boolean(error)
}
